import { A } from '@solidjs/router';
import {
  type Component,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  Show,
} from 'solid-js';
import { deleteProject, fetchProjects } from '../services/api';

type ViewMode = 'card' | 'table';
type PageSize = 30 | 50 | 100;

const Projects: Component = () => {
  // Pagination state
  const [pageSize, setPageSize] = createSignal<PageSize>(30);
  const [currentPage, setCurrentPage] = createSignal(1);

  // Filter state
  const [selectedStatus, setSelectedStatus] = createSignal<string>('');
  const [selectedVisibility, setSelectedVisibility] = createSignal<string>('');
  const [searchQuery, setSearchQuery] = createSignal('');

  // UI state
  const [viewMode, setViewMode] = createSignal<ViewMode>('card');
  const [selectedIds, setSelectedIds] = createSignal<Set<string>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = createSignal(false);
  const [isDeleting, setIsDeleting] = createSignal(false);
  const [_showFilters, _setShowFilters] = createSignal(false);

  // Computed pagination params
  const paginationParams = createMemo(() => ({
    limit: pageSize(),
    offset: (currentPage() - 1) * pageSize(),
    status: selectedStatus() || undefined,
    visibility: selectedVisibility() || undefined,
    search: searchQuery() || undefined,
  }));

  // Fetch projects with pagination
  const [projectsData, { refetch }] = createResource(paginationParams, fetchProjects);

  // Derived values
  const projects = createMemo(() => projectsData()?.items || []);
  const totalCount = createMemo(() => projectsData()?.total || 0);
  const totalPages = createMemo(() => Math.ceil(totalCount() / pageSize()));

  // Reset to page 1 when filters change
  createEffect(() => {
    selectedStatus();
    selectedVisibility();
    searchQuery();
    pageSize();
    setCurrentPage(1);
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const toggleSelection = (projectId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const selectAll = () => {
    const all = projects();
    if (all && all.length > 0) {
      setSelectedIds(new Set(all.map((p) => p.id)));
    }
  };

  const clearFilters = () => {
    setSelectedStatus('');
    setSelectedVisibility('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const hasActiveFilters = createMemo(
    () => selectedStatus() !== '' || selectedVisibility() !== '' || searchQuery() !== '',
  );

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleDeleteClick = () => {
    if (selectedIds().size > 0) {
      setShowConfirmDialog(true);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      for (const id of selectedIds()) {
        await deleteProject(id);
      }
      setSelectedIds(new Set());
      setShowConfirmDialog(false);
      refetch();
    } catch (error) {
      console.error('Failed to delete projects:', error);
      alert('Failed to delete projects. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowConfirmDialog(false);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'draft':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'paused':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'archived':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getVisibilityBadgeClass = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'group':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'private':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getRunModeBadge = (runMode: string) => {
    switch (runMode) {
      case 'buy':
        return {
          label: 'Buy',
          class: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
        };
      case 'run':
        return {
          label: 'Run',
          class: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
        };
      case 'both':
        return {
          label: 'Buy & Run',
          class: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
        };
      default:
        return {
          label: runMode,
          class: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        };
    }
  };

  return (
    <div>
      {/* Confirmation Dialog */}
      <Show when={showConfirmDialog()}>
        <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-gray-800 rounded-lg p-6 max-w-md text-center border border-gray-700">
            <div class="mb-4 flex justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <h2 class="text-xl font-medium mb-4">Delete Projects</h2>
            <p class="text-gray-400 mb-6">
              Are you sure you want to delete {selectedIds().size} project
              {selectedIds().size !== 1 ? 's' : ''}? This action cannot be undone.
            </p>
            <div class="flex justify-center gap-4">
              <button
                type="button"
                onClick={cancelDelete}
                disabled={isDeleting()}
                class="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting()}
                class="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50"
              >
                {isDeleting() ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Page Header */}
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Projects
            </h1>
            <p class="text-gray-400 text-sm mt-1">Create and manage your AI agent projects</p>
          </div>
          <A
            href="/projects/new"
            class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="mr-2"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            New Project
          </A>
        </div>
      </div>

      {/* Filters and Controls */}
      <div class="mb-6 bg-gray-800/50 rounded-lg border border-gray-700 p-4">
        <div class="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div class="flex-1 min-w-[200px]">
            <div class="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery()}
                onInput={(e) => setSearchQuery(e.currentTarget.value)}
                class="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={selectedStatus()}
            onChange={(e) => setSelectedStatus(e.currentTarget.value)}
            class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </select>

          {/* Visibility Filter */}
          <select
            value={selectedVisibility()}
            onChange={(e) => setSelectedVisibility(e.currentTarget.value)}
            class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500"
          >
            <option value="">All Visibility</option>
            <option value="private">Private</option>
            <option value="group">Group</option>
            <option value="public">Public</option>
          </select>

          {/* View Mode Toggle */}
          <div class="flex rounded-lg overflow-hidden border border-gray-600">
            <button
              type="button"
              onClick={() => setViewMode('card')}
              class={`px-3 py-2 ${
                viewMode() === 'card'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-900/50 text-gray-400 hover:text-gray-200'
              }`}
              title="Card View"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect width="7" height="7" x="3" y="3" rx="1" />
                <rect width="7" height="7" x="14" y="3" rx="1" />
                <rect width="7" height="7" x="14" y="14" rx="1" />
                <rect width="7" height="7" x="3" y="14" rx="1" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              class={`px-3 py-2 ${
                viewMode() === 'table'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-900/50 text-gray-400 hover:text-gray-200'
              }`}
              title="Table View"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>

          {/* Clear Filters */}
          <Show when={hasActiveFilters()}>
            <button
              type="button"
              onClick={clearFilters}
              class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm transition-colors"
            >
              Clear Filters
            </button>
          </Show>
        </div>

        {/* Selection Actions */}
        <Show when={selectedIds().size > 0}>
          <div class="mt-4 pt-4 border-t border-gray-700 flex items-center gap-4">
            <span class="text-gray-400 text-sm">{selectedIds().size} selected</span>
            <button
              type="button"
              onClick={clearSelection}
              class="text-sm text-gray-400 hover:text-gray-200 transition-colors"
            >
              Clear selection
            </button>
            <button
              type="button"
              onClick={handleDeleteClick}
              class="text-sm text-red-400 hover:text-red-300 transition-colors"
            >
              Delete selected
            </button>
          </div>
        </Show>
      </div>

      {/* Results Count and Page Size */}
      <div class="mb-4 flex items-center justify-between text-sm text-gray-400">
        <span>
          {totalCount()} project{totalCount() !== 1 ? 's' : ''} found
        </span>
        <div class="flex items-center gap-2">
          <span>Show:</span>
          <select
            value={pageSize()}
            onChange={(e) => setPageSize(Number(e.currentTarget.value) as PageSize)}
            class="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300 focus:outline-none focus:border-purple-500"
          >
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      <Show when={projectsData.loading}>
        <div class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
        </div>
      </Show>

      {/* Empty State */}
      <Show when={!projectsData.loading && projects().length === 0}>
        <div class="text-center py-12">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mx-auto text-gray-600 mb-4"
          >
            <path d="M3 3v18h18" />
            <path d="m19 9-5 5-4-4-3 3" />
          </svg>
          <h3 class="text-xl font-medium text-gray-300 mb-2">No projects found</h3>
          <p class="text-gray-500 mb-6">
            {hasActiveFilters()
              ? 'Try adjusting your filters'
              : 'Create your first project to get started'}
          </p>
          <Show when={!hasActiveFilters()}>
            <A
              href="/projects/new"
              class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
            >
              Create Project
            </A>
          </Show>
        </div>
      </Show>

      {/* Card View */}
      <Show when={!projectsData.loading && viewMode() === 'card' && projects().length > 0}>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <For each={projects()}>
            {(project) => (
              <div
                class={`bg-gray-800/50 rounded-lg border ${
                  selectedIds().has(project.id)
                    ? 'border-purple-500'
                    : 'border-gray-700 hover:border-gray-600'
                } transition-all`}
              >
                <div class="p-4">
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex-1">
                      <A
                        href={`/projects/${project.id}`}
                        class="text-lg font-medium text-gray-200 hover:text-purple-400 transition-colors line-clamp-1"
                      >
                        {project.name}
                      </A>
                      <p class="text-gray-500 text-sm mt-1 line-clamp-2">{project.goal}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedIds().has(project.id)}
                      onChange={() => toggleSelection(project.id)}
                      class="ml-3 h-4 w-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
                    />
                  </div>

                  <div class="flex flex-wrap gap-2 mb-3">
                    <span
                      class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                        project.status,
                      )}`}
                    >
                      {project.status}
                    </span>
                    <span
                      class={`px-2 py-0.5 rounded-full text-xs border ${getVisibilityBadgeClass(
                        project.visibility,
                      )}`}
                    >
                      {project.visibility}
                    </span>
                    <span
                      class={`px-2 py-0.5 rounded-full text-xs border ${
                        getRunModeBadge(project.run_mode).class
                      }`}
                    >
                      {getRunModeBadge(project.run_mode).label}
                    </span>
                  </div>

                  <div class="flex items-center justify-between text-xs text-gray-500">
                    <span>v{project.version}</span>
                    <span>{formatDate(project.updated_at)}</span>
                  </div>
                </div>

                <div class="border-t border-gray-700 px-4 py-2 flex items-center justify-end gap-2">
                  <A
                    href={`/projects/${project.id}`}
                    class="text-sm text-gray-400 hover:text-gray-200 transition-colors"
                  >
                    View
                  </A>
                  <A
                    href={`/projects/${project.id}/edit`}
                    class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Edit
                  </A>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Table View */}
      <Show when={!projectsData.loading && viewMode() === 'table' && projects().length > 0}>
        <div class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
          <table class="w-full">
            <thead class="bg-gray-900/50 border-b border-gray-700">
              <tr>
                <th class="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds().size === projects().length && projects().length > 0}
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        selectAll();
                      } else {
                        clearSelection();
                      }
                    }}
                    class="h-4 w-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
                  />
                </th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Name</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Visibility</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Mode</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Version</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Updated</th>
                <th class="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-700">
              <For each={projects()}>
                {(project) => (
                  <tr
                    class={`hover:bg-gray-800/50 transition-colors ${
                      selectedIds().has(project.id) ? 'bg-purple-900/20' : ''
                    }`}
                  >
                    <td class="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds().has(project.id)}
                        onChange={() => toggleSelection(project.id)}
                        class="h-4 w-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
                      />
                    </td>
                    <td class="px-4 py-3">
                      <A
                        href={`/projects/${project.id}`}
                        class="text-gray-200 hover:text-purple-400 transition-colors font-medium"
                      >
                        {project.name}
                      </A>
                      <p class="text-gray-500 text-xs mt-0.5 line-clamp-1">{project.goal}</p>
                    </td>
                    <td class="px-4 py-3">
                      <span
                        class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                          project.status,
                        )}`}
                      >
                        {project.status}
                      </span>
                    </td>
                    <td class="px-4 py-3">
                      <span
                        class={`px-2 py-0.5 rounded-full text-xs border ${getVisibilityBadgeClass(
                          project.visibility,
                        )}`}
                      >
                        {project.visibility}
                      </span>
                    </td>
                    <td class="px-4 py-3">
                      <span
                        class={`px-2 py-0.5 rounded-full text-xs border ${
                          getRunModeBadge(project.run_mode).class
                        }`}
                      >
                        {getRunModeBadge(project.run_mode).label}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-400">v{project.version}</td>
                    <td class="px-4 py-3 text-sm text-gray-500">
                      {formatDate(project.updated_at)}
                    </td>
                    <td class="px-4 py-3 text-right">
                      <div class="flex items-center justify-end gap-2">
                        <A
                          href={`/projects/${project.id}`}
                          class="text-sm text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          View
                        </A>
                        <A
                          href={`/projects/${project.id}/edit`}
                          class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                        >
                          Edit
                        </A>
                      </div>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>

      {/* Pagination */}
      <Show when={totalPages() > 1}>
        <div class="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage() === 1}
            class="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          <For
            each={Array.from({ length: Math.min(5, totalPages()) }, (_, i) => {
              const page = currentPage();
              const total = totalPages();
              if (total <= 5) return i + 1;
              if (page <= 3) return i + 1;
              if (page >= total - 2) return total - 4 + i;
              return page - 2 + i;
            })}
          >
            {(page) => (
              <button
                type="button"
                onClick={() => setCurrentPage(page)}
                class={`px-3 py-2 rounded-lg border transition-colors ${
                  currentPage() === page
                    ? 'bg-purple-600 border-purple-500 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {page}
              </button>
            )}
          </For>

          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages(), p + 1))}
            disabled={currentPage() === totalPages()}
            class="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </Show>
    </div>
  );
};

export default Projects;
